import {Injectable} from "@angular/core";
import {ImageApi} from "@webmate-web-core/shared/api/rest/image.restapi";
import {FactApi} from "@webmate-web-core/shared/api/rest/fact.restapi";
import {defer, EMPTY, merge, Observable, of, throwError} from "rxjs";
import {FactSessionId, ProjectId, ScreenshotDataUrl, ScreenshotId} from "@webmate-web-core/shared/types/ids";
import {
    FactGenerationMode,
    FactSessionFactStreamMessage,
    FactSessionInfo,
    FactSessionStates,
    ScopedFactRequest,
    WellKnownFactEvents
} from "@webmate-web-core/shared/types/fact";
import {
    Artifact,
    ArtifactAssociation,
    ArtifactInfo,
    ArtifactType,
    WellKnownArtifactTypes
} from "@webmate-web-core/shared/types/artifact";
import {
    catchError,
    distinctUntilChanged,
    exhaustMap,
    filter,
    map,
    shareReplay,
    switchMap,
    take,
    takeUntil,
    tap
} from "rxjs/operators";
import {EventsService} from "@webmate-web-core/shared/services/events/events.service";
import {ArtifactService} from "@webmate-web-core/shared/services/artifact.service";
import {dataUrlToFile} from "@webmate-web-core/shared/utils/image-dataurl";
import {WebSocketService} from "@webmate-web-core/shared/services/events/websocket.service";
import _ from 'lodash';

@Injectable({
    providedIn: 'root'
})
export class FactService {
    constructor (private api: FactApi,
                 private imageApi: ImageApi,
                 private eventService: EventsService,
                 private artifactService: ArtifactService,
                 private webSocketService: WebSocketService) {}

    generateFact(projectId: ProjectId, goals: ScopedFactRequest[], factGenerationMode: FactGenerationMode): Observable<FactSessionId> {
        return this.api.generateFacts(projectId, goals, [], factGenerationMode);
    }

    generateArtifactUpdates(
        projectId: ProjectId,
        goal: ScopedFactRequest,
        factGenerationMode: FactGenerationMode,
        artifactType: ArtifactType,
        onFactSessionUpdate?: (session: FactSessionInfo) => void
    ): Observable<Artifact> {
        return this.generateFact(projectId, [goal], factGenerationMode).pipe(
            switchMap(factSessionId =>
                this.observeArtifactUpdates(projectId, factSessionId, artifactType, onFactSessionUpdate))
        );
    }

    /** Emits artifact versions until the fact session ends. */
    observeArtifactUpdates(
        projectId: ProjectId,
        factSessionId: FactSessionId,
        artifactType: ArtifactType,
        onFactSessionUpdate?: (session: FactSessionInfo) => void
    ): Observable<Artifact> {
        return defer(() => {
            const factSessionAssociation: ArtifactAssociation = {
                id: factSessionId,
                associationType: 'FactSession',
                relationName: 'PartOf'
            };
            const artifactMatches = (artifact: ArtifactInfo) =>
                artifact.artifactType === artifactType &&
                artifact.associations.some(artifactAssociation =>
                    artifactAssociation.id === factSessionAssociation.id &&
                    artifactAssociation.associationType === factSessionAssociation.associationType &&
                    artifactAssociation.relationName === factSessionAssociation.relationName
                );

            const session$ = this.observeFactSession(projectId, factSessionId).pipe(
                tap(session => onFactSessionUpdate?.(session)),
                shareReplay({bufferSize: 1, refCount: true})
            );

            const sessionEnded$ = session$.pipe(
                filter(session => session.state === FactSessionStates.Finished
                    || session.state === FactSessionStates.Terminated
                    || session.state === FactSessionStates.Failed),
                take(1),
                shareReplay({bufferSize: 1, refCount: true})
            );

            const partialResults$ = session$.pipe(
                takeUntil(sessionEnded$),
                exhaustMap(() => this.findFactSessionArtifact$(projectId, artifactType, artifactMatches).pipe(
                    filter((artifact): artifact is ArtifactInfo => !!artifact),
                    switchMap(artifact => this.artifactService.refreshArtifact$(artifact.id).pipe(
                        catchError(error => {
                            console.warn(`Could not refresh artifact ${artifact.id}`, error);
                            return EMPTY;
                        })
                    ))
                ))
            );

            // Check once more after termination for the final artifact version.
            const finalResult$ = sessionEnded$.pipe(
                switchMap(session => this.findFactSessionArtifact$(projectId, artifactType, artifactMatches).pipe(
                    switchMap(artifact => artifact
                        ? this.artifactService.refreshArtifact$(artifact.id)
                        : throwError(() => new Error(
                            `Fact session ${factSessionId} ended as ${session.state} without producing an artifact`)))
                ))
            );

            // Keep the shared session alive while switching from partial to final results.
            return merge(partialResults$, finalResult$).pipe(
                distinctUntilChanged((previous, current) =>
                    previous.id === current.id &&
                    previous.version !== undefined &&
                    current.version !== undefined &&
                    previous.version === current.version)
            );
        });
    }

    private findFactSessionArtifact$(
        projectId: ProjectId,
        artifactType: ArtifactType,
        matches: (artifact: ArtifactInfo) => boolean
    ): Observable<ArtifactInfo | undefined> {
        return this.artifactService.getArtifactsInProject$(projectId, undefined, [artifactType]).pipe(
            take(1),
            catchError(error => {
                console.warn(`Could not look up ${artifactType} artifacts in project ${projectId}`, error);
                return of([] as ArtifactInfo[]);
            }),
            map(artifacts => artifacts.find(matches))
        );
    }

    observeFactSession(projectId: ProjectId, factSessionId: FactSessionId): Observable<FactSessionInfo> {
        return defer(() => {
            let liveSnapshotReceived = false;
            const liveUpdates$ = this.eventService
                .getEventsOfTypeForProject(projectId, WellKnownFactEvents.FactSessionUpdated)
                .pipe(
                    filter(event => event.info?.factSessionId === factSessionId),
                    map(event => this.normalizeFactSession(event.info as FactSessionInfo)),
                    tap(() => liveSnapshotReceived = true)
                );
            const persistedSnapshot$ = this.api.getFactSession(factSessionId).pipe(
                filter(() => !liveSnapshotReceived),
                map(info => this.normalizeFactSession(info)),
                catchError(() => EMPTY)
            );

            // Subscribe to live updates first so an older REST response cannot replace one.
            return merge(liveUpdates$, persistedSnapshot$);
        }).pipe(
            distinctUntilChanged((previous, current) => _.isEqual(previous, current)),
            shareReplay({bufferSize: 1, refCount: true})
        );
    }

    getFactSessionInfo$(factSessionId: FactSessionId): Observable<FactSessionInfo> {
        return this.api.getFactSession(factSessionId);
    }

    observeFactSessionFacts$(factSessionId: FactSessionId): Observable<FactSessionFactStreamMessage> {
        return this.webSocketService.connectToFactSessionFacts(factSessionId).pipe(
            filter(event => event.data !== 'WMWebSocketPong' && event.data !== 'WMWebSocketTerminate'),
            map(event => JSON.parse(event.data) as FactSessionFactStreamMessage)
        );
    }

    analyzeContrast(
        projectId: ProjectId,
        imageId?: ScreenshotId,
        imageUrl?: ScreenshotDataUrl
    ): Observable<Artifact> {
        const imageId$ = imageId
            ? of(imageId)
            : imageUrl
                ? this.storeImageFromUrl(imageUrl, 'screenshot.png', projectId)
                : throwError(() => new Error('Either imageId or imageUrl must be provided'));

        const goal: ScopedFactRequest = {
            request: {
                factType: { artifactType: WellKnownArtifactTypes.ImageTextContrastAnalysis },
                params: { value: { imageId: undefined as unknown as ScreenshotId } }, // overwritten below
            },
            scope: { type: 'GlobalScope' },
        };

        return imageId$.pipe(
            switchMap((resolvedImageId) =>
                this.generateFact(
                    projectId,
                    [
                        {
                            ...goal,
                            request: {
                                ...goal.request,
                                params: { value: { imageId: resolvedImageId } },
                            },
                        },
                    ],
                    FactGenerationMode.Query
                )
            ),

            switchMap((factSessionId) =>
                this.eventService
                    .getEventsOfTypeForProject(projectId, WellKnownFactEvents.FactSessionUpdated)
                    .pipe(
                        filter(
                            (event) =>
                                event.info.factSessionId === factSessionId &&
                                event.info.state === FactSessionStates.Finished
                        ),
                        map((event) => event.info.facts),
                        filter((facts) => facts.length === 1),
                        map((facts) => facts[0].artifactId),
                        take(1) // important: complete once we got the finished session
                    )
            ),

            // Now fetch and RETURN the artifact
            switchMap((artifactId) => this.artifactService.getArtifact$(artifactId).pipe(take(1)))
        );
    }

    private storeImageFromUrl(url: ScreenshotDataUrl, filename: string, projectId: ProjectId): Observable<ScreenshotId> {
        return this.imageApi.storeImage(dataUrlToFile(url, filename), projectId) as Observable<ScreenshotId>;
    }

    private normalizeFactSession(info: FactSessionInfo): FactSessionInfo {
        return {
            ...info,
            processInfo: Object.fromEntries(
                Object.entries(info.processInfo ?? {}).map(([id, process]) => [
                    id,
                    {...process, warnings: process.warnings ?? []}
                ])
            )
        };
    }
}
